import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";

import { menus } from "./lib/menus";
import { ThemeToggle } from "./components/theme";
import { SearchPanel } from "./components/search";
import { SearchBtns } from "./components/search-btns";
import { SelectPanel } from "./components/select-group";

import { Table } from "./components/table";
import { Cards } from "./components/cards";
import { TableSkeleton, CardSkeleton } from "./components/skeleton";

const API_URL = process.env.REACT_APP_API_URL || '/api';

export const Main = () => {
    // 高専Webシラバスの一関高専のURL
    const SYLLABUS_BASE_URL = "https://syllabus.kosen-k.go.jp/Pages/PublicSyllabus?school_id=06&";

    const mode = window.innerWidth >= 1024 ? 'table' : 'card';
    const [viewMode, setViewMode] = useState(mode);
    const [isFilterOpen, setIsFilterOpen] = useState(false);

    // searching states
    const [keyword, setKeyword] = useState('');
    const [activeFilters, setActiveFilters] = useState(() => 
        Object.fromEntries(menus.map(group => [group.key, []]))
    );
    const [viewCount, setViewCount] = useState(20);

    const [response, setResponse] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(0);
    const [loading, setLoading] = useState(false);
    const [isInitialized, setIsInitialized] = useState(false);

    // Searching
    const performSearch = useCallback(async (overrideKeyword, targetPage = currentPage, limit = viewCount) => {
        setLoading(true);

        // overrideKeywordは、stateの更新を待たずに空のキーワードを渡すため (search.js)
        const searchKeyword = overrideKeyword !== undefined ? overrideKeyword : keyword;
        const queryParams = {
            page: targetPage + 1, // 1-based index
            limit: limit,
        }

        if (searchKeyword.trim()) {
            queryParams.q = searchKeyword.trim();
        }

        Object.entries(activeFilters || {}).forEach(([key, values]) => {
            if (values && values.length > 0) {
                queryParams[key] = values;
            }
        });

        try {
            const res = await axios.get(`${API_URL}/subjects`, { 
                params: queryParams, 
                paramsSerializer: {
                    indexes: null
                }
            });
            setResponse(res.data.rows || []);
            setTotalCount(res.data.total || 0);
        } catch (error) {
            console.error('❌ API Error: ', error.response?.data || error.message);
        } finally {
            setLoading(false);
            setIsInitialized(true);
        }
    }, [keyword, activeFilters, currentPage, viewCount]);

    // observing in real-time
    // definitely return to 1 page when filters change
    useEffect(() => {
        setCurrentPage(0);
        performSearch(undefined, 0);
        // eslint-disable-next-line
    }, [activeFilters, viewCount]);

    // search when page changes
    useEffect(() => {
        performSearch();
        // eslint-disable-next-line
    }, [currentPage]);

    const totalPages = useMemo(() => Math.ceil(totalCount / viewCount), [totalCount, viewCount]);

    // chips toggle filter
    // categoryKey: e.g. "grade", optionValue: e.g. "1"
    const toggleFilter = (categoryKey, optionValue) => {
        setActiveFilters(prev => {
            const currentList = (prev || {})[categoryKey] || [];
            const nextList = currentList.includes(optionValue)
                ? currentList.filter(v => v !== optionValue)
                : [...currentList, optionValue];
            return { ...prev, [categoryKey]: nextList };
        });
    };

    return (
        <main className="flex flex-col min-h-screen bg-gray-200 dark:bg-zinc-900 px-6  py-3">
            <header className="flex flex-row justify-between items-center">
                <a 
                    href="./" 
                    className="relative text-2xl font-bold focus:outline-none select-none"
                    aria-label="New Syllabus"
                >
                    <span className="bg-gradient-to-r from-blue-500 to-red-500 bg-clip-text text-transparent transition-opacity duration-300 dark:opacity-0">
                        New Syllabus
                    </span>
                    <span className="absolute inset-0 bg-gradient-to-r from-blue-400 to-red-400 bg-clip-text text-transparent opacity-0 transition-opacity duration-300 dark:opacity-100">
                        New Syllabus
                    </span>
                </a>
                
                {/* </button> */}
                <ThemeToggle />
            </header>

            <div className="flex flex-col gap-4 my-3">
                {/* Input Field */}
                <div className="flex flex-col gap-3">
                    <SearchPanel 
                        performSearch={performSearch} 
                        keyword={keyword} setKeyword={setKeyword} 
                        setCurrentPage={setCurrentPage}
                    />
                </div>
                
                <div className="flex flex-row gap-3 xl:gap-6">
                    {/* Side Filter Panel */}
                    <div className="hidden lg:visible lg:flex lg:flex-col w-[378px] lg:shrink-0">
                        <div className="grid grid-cols-1 gap-4">
                            <SelectPanel isFilterOpen={isFilterOpen} toggleFilter={toggleFilter} activeFilters={activeFilters} />
                        </div>
                    </div>

                    <div className="flex flex-col gap-4 min-w-0 flex-grow">
                        {/* Option Field */}
                        <SearchBtns
                            currentMode={viewMode}
                            onToggle={setViewMode}
                            isFilterOpen={isFilterOpen}
                            onFilterToggle={setIsFilterOpen}
                            toggleFilter={toggleFilter}
                            activeFilters={activeFilters}
                            setActiveFilters={setActiveFilters}
                            viewCount={viewCount}
                            setViewCount={setViewCount}
                            totalPages={totalPages}
                            currentPage={currentPage}
                            setCurrentPage={setCurrentPage}
                        />
                        {/* Subjects Field */}
                        <div className="flex flex-col flex-1 gap-3 min-w-0">
                            {/* 初回アクセス時にスケルトンを表示 */}
                            {loading && !isInitialized ? (
                                viewMode === 'table' ? (
                                    <TableSkeleton />
                                ) : (
                                    <CardSkeleton />
                                )
                            ) : (
                                response.length === 0 ? (
                                    <div className="bg-white dark:bg-zinc-700 rounded-3xl border-2 border-dashed border-slate-300 dark:border-slate-400 py-20 text-center">
                                        <p className="text-slate-400 dark:text-slate-200 font-bold">該当する科目がありません</p>
                                    </div>
                                ) : (
                                    viewMode === 'table' ? (
                                        <Table subjects={response} SYLLABUS_BASE_URL={SYLLABUS_BASE_URL} />
                                    ) : (
                                        <Cards subjects={response} SYLLABUS_BASE_URL={SYLLABUS_BASE_URL} />
                                    )
                                )
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </main>
    );
}